import { format } from "date-fns";
import type { UseQueryOptions } from "@tanstack/react-query";
import { apiRequest, type ApiError, useApiQuery } from "@/shared/api";
import type {
  TransactionHistoryFilters as TransactionHistoryFiltersType,
  TransactionHistoryItem,
  TransactionHistoryResponse,
} from "../interfaces/transaction.interface";
import type { IDepositHistory } from "../groups/assets/interfaces/deposit.interface";
import type { IWithdrawalHistory } from "../groups/assets/interfaces/withdrawal.interface";

export type TransactionHistoryFilterOptions = {
  type?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
};

type ApiQueryOptions<TData, TQueryKey extends readonly unknown[]> = Omit<
  UseQueryOptions<TData, ApiError, TData, TQueryKey>,
  "queryKey" | "queryFn"
>;

const buildQueryKey = (filters: TransactionHistoryFiltersType) =>
  ["transactions", filters.month, filters.year] as const;

const buildDepositQueryKey = (filters: TransactionHistoryFiltersType) =>
  ["cryptoDepositHistory", filters.month, filters.year] as const;

const buildWithdrawalQueryKey = (filters: TransactionHistoryFiltersType) =>
  ["cryptoWithdrawalHistory", filters.month, filters.year] as const;

const buildFiatDepositQueryKey = (filters: TransactionHistoryFiltersType) =>
  ["fiatDepositHistory", filters.month, filters.year] as const;

const buildFiatWithdrawalQueryKey = (filters: TransactionHistoryFiltersType) =>
  ["fiatWithdrawalHistory", filters.month, filters.year] as const;

const transactionHistoryRequest = (filters: TransactionHistoryFiltersType) => ({
  url: "/transactions",
  method: "get" as const,
  params: {
    month: filters.month,
    year: filters.year,
  },
});

export const getTransactionHistory = (filters: TransactionHistoryFiltersType) =>
  apiRequest<TransactionHistoryResponse>(transactionHistoryRequest(filters));

const cryptoDepositHistoryRequest = (
  filters: TransactionHistoryFiltersType
) => ({
  url: "/crypto/deposit/history",
  method: "get" as const,
  params: {
    month: filters.month,
    year: filters.year,
  },
});

export const getCryptoDepositHistory = (
  filters: TransactionHistoryFiltersType
) =>
  apiRequest<TransactionHistoryResponse>(cryptoDepositHistoryRequest(filters));

const cryptoWithdrawalHistoryRequest = (
  filters: TransactionHistoryFiltersType
) => ({
  url: "/crypto/withdrawal/history",
  method: "get" as const,
  params: {
    month: filters.month,
    year: filters.year,
  },
});

export const getCryptoWithdrawalHistory = (
  filters: TransactionHistoryFiltersType
) =>
  apiRequest<TransactionHistoryResponse>(
    cryptoWithdrawalHistoryRequest(filters)
  );

type FiatDepositHistoryApiItem = {
  id?: string;
  type?: string;
  amount?: string | number;
  currency?: string;
  status?: string;
  timestamp?: string;
  description?: string;
  reference?: string;
};

type FiatDepositHistoryApiResponse = FiatDepositHistoryApiItem[];

const fiatDepositHistoryRequest = (filters: TransactionHistoryFiltersType) => ({
  url: "/fiat/deposit/history",
  method: "get" as const,
  params: {
    month: filters.month,
    year: filters.year,
  },
});

const parseAmountValue = (value: unknown) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const normalizeString = (value: unknown) =>
  typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : undefined;

const mapFiatDepositItemToTransaction = (
  item: FiatDepositHistoryApiItem,
  index: number
): TransactionHistoryItem => {
  const id =
    normalizeString(item.id) ??
    normalizeString(item.reference) ??
    `${normalizeString(item.type) ?? "fiatDeposit"}-${index}`;

  return {
    id,
    createdAt: normalizeString(item.timestamp) ?? new Date().toISOString(),
    type: normalizeString(item.type) ?? "fiatDeposit",
    amount: parseAmountValue(item.amount),
    currency: normalizeString(item.currency) ?? "",
    status: normalizeString(item.status) ?? "",
    transferTitle:
      normalizeString(item.description) ?? normalizeString(item.reference),
    sourceAccountNumber: normalizeString(item.reference),
    amountFrom: null,
    currencyFrom: null,
  };
};

export const mapFiatDepositHistoryToTransactions = (
  data?: FiatDepositHistoryApiResponse
): TransactionHistoryResponse =>
  (data ?? []).map(mapFiatDepositItemToTransaction);

type FiatWithdrawalHistoryApiItem = {
  id?: string;
  type?: string;
  amount?: string | number;
  currency?: string;
  status?: string;
  timestamp?: string;
  description?: string;
  reference?: string;
};

type FiatWithdrawalHistoryApiResponse = FiatWithdrawalHistoryApiItem[];

const fiatWithdrawalHistoryRequest = (
  filters: TransactionHistoryFiltersType
) => ({
  url: "/fiat/withdrawal/history",
  method: "get" as const,
  params: {
    month: filters.month,
    year: filters.year,
  },
});

const mapFiatWithdrawalItemToTransaction = (
  item: FiatWithdrawalHistoryApiItem,
  index: number
): TransactionHistoryItem => {
  const id =
    normalizeString(item.id) ??
    normalizeString(item.reference) ??
    `${normalizeString(item.type) ?? "fiatWithdrawal"}-${index}`;

  return {
    id,
    createdAt: normalizeString(item.timestamp) ?? new Date().toISOString(),
    type: normalizeString(item.type) ?? "fiatWithdrawal",
    amount: parseAmountValue(item.amount),
    currency: normalizeString(item.currency) ?? "",
    status: normalizeString(item.status) ?? "",
    transferTitle:
      normalizeString(item.description) ?? normalizeString(item.reference),
    sourceAccountNumber: normalizeString(item.reference),
    amountFrom: null,
    currencyFrom: null,
  };
};

export const mapFiatWithdrawalHistoryToTransactions = (
  data?: FiatWithdrawalHistoryApiResponse
): TransactionHistoryResponse =>
  (data ?? []).map(mapFiatWithdrawalItemToTransaction);

export const getFiatDepositHistory = (filters: TransactionHistoryFiltersType) =>
  apiRequest<FiatDepositHistoryApiResponse>(fiatDepositHistoryRequest(filters));

export const useFiatDepositHistory = (
  filters: TransactionHistoryFiltersType,
  options?: ApiQueryOptions<
    FiatDepositHistoryApiResponse,
    ReturnType<typeof buildFiatDepositQueryKey>
  >
) => {
  const queryKey = buildFiatDepositQueryKey(filters);

  return useApiQuery<
    FiatDepositHistoryApiResponse,
    ApiError,
    ReturnType<typeof buildFiatDepositQueryKey>
  >(queryKey, fiatDepositHistoryRequest(filters), options);
};

export const getFiatWithdrawalHistory = (
  filters: TransactionHistoryFiltersType
) =>
  apiRequest<FiatWithdrawalHistoryApiResponse>(
    fiatWithdrawalHistoryRequest(filters)
  );

export const useFiatWithdrawalHistory = (
  filters: TransactionHistoryFiltersType,
  options?: ApiQueryOptions<
    FiatWithdrawalHistoryApiResponse,
    ReturnType<typeof buildFiatWithdrawalQueryKey>
  >
) => {
  const queryKey = buildFiatWithdrawalQueryKey(filters);

  return useApiQuery<
    FiatWithdrawalHistoryApiResponse,
    ApiError,
    ReturnType<typeof buildFiatWithdrawalQueryKey>
  >(queryKey, fiatWithdrawalHistoryRequest(filters), options);
};

export const useTransactionHistory = (
  filters: TransactionHistoryFiltersType,
  options?: ApiQueryOptions<
    TransactionHistoryResponse,
    ReturnType<typeof buildQueryKey>
  >
) => {
  const queryKey = buildQueryKey(filters);

  return useApiQuery<
    TransactionHistoryResponse,
    ApiError,
    ReturnType<typeof buildQueryKey>
  >(queryKey, transactionHistoryRequest(filters), options);
};

export const useCryptoDepositHistory = (
  filters: TransactionHistoryFiltersType,
  options?: ApiQueryOptions<
    TransactionHistoryResponse,
    ReturnType<typeof buildDepositQueryKey>
  >
) => {
  const queryKey = buildDepositQueryKey(filters);

  return useApiQuery<
    TransactionHistoryResponse,
    ApiError,
    ReturnType<typeof buildDepositQueryKey>
  >(queryKey, cryptoDepositHistoryRequest(filters), options);
};

export const useCryptoWithdrawalHistory = (
  filters: TransactionHistoryFiltersType,
  options?: ApiQueryOptions<
    TransactionHistoryResponse,
    ReturnType<typeof buildWithdrawalQueryKey>
  >
) => {
  const queryKey = buildWithdrawalQueryKey(filters);

  return useApiQuery<
    TransactionHistoryResponse,
    ApiError,
    ReturnType<typeof buildWithdrawalQueryKey>
  >(queryKey, cryptoWithdrawalHistoryRequest(filters), options);
};

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  sepaTransfer: "SEPA Transfer",
  sepaDeposit: "SEPA Deposit",
  exchange: "Exchange",
  cryptoDeposit: "Crypto Deposit",
  cryptoWithdrawal: "Crypto Withdrawal",
  fiatDeposit: "Fiat Deposit",
  fiatWithdrawal: "Fiat Withdrawal",
};

export const DEPOSIT_TRANSACTION_TYPES = [
  "sepaDeposit",
  "cryptoDeposit",
  "fiatDeposit",
  "exchange",
] as const;

export const WITHDRAWAL_TRANSACTION_TYPES = [
  "sepaTransfer",
  "cryptoWithdrawal",
  "fiatWithdrawal",
] as const;

const DEPOSIT_TRANSACTION_TYPE_SET = new Set<string>(DEPOSIT_TRANSACTION_TYPES);
const WITHDRAWAL_TRANSACTION_TYPE_SET = new Set<string>(
  WITHDRAWAL_TRANSACTION_TYPES
);

const formatTransactionType = (type: string) =>
  TRANSACTION_TYPE_LABELS[type] ??
  type.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());

const formatStatus = (status: string) =>
  status
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatCurrency = (currency?: string) =>
  currency?.trim() ? currency.trim().toUpperCase() : "—";

const formatDateTime = (value: string) => {
  try {
    return format(new Date(value), "dd/MM/yyyy HH:mm");
  } catch {
    return value;
  }
};

const resolveWalletDetail = (transaction: TransactionHistoryItem) =>
  transaction.transferTitle ??
  transaction.sourceAccountNumber ??
  transaction.currencyFrom ??
  "—";

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const createTransactionPredicate = (
  options?: TransactionHistoryFilterOptions
) => {
  if (!options) {
    return () => true;
  }

  const { type, status } = options;
  const startMs = options.startDate ? Date.parse(options.startDate) : undefined;
  const rawEndMs = options.endDate ? Date.parse(options.endDate) : undefined;
  const endMs =
    rawEndMs !== undefined && !Number.isNaN(rawEndMs)
      ? rawEndMs + DAY_IN_MS - 1
      : undefined;
  const normalizedStartMs =
    startMs !== undefined && !Number.isNaN(startMs) ? startMs : undefined;

  return (transaction: TransactionHistoryItem) => {
    if (type && transaction.type !== type) {
      return false;
    }

    if (status && transaction.status !== status) {
      return false;
    }

    const createdAtMs = Date.parse(transaction.createdAt);
    if (!Number.isNaN(createdAtMs)) {
      if (normalizedStartMs !== undefined && createdAtMs < normalizedStartMs) {
        return false;
      }

      if (endMs !== undefined && createdAtMs > endMs) {
        return false;
      }
    }

    return true;
  };
};

const mapToDepositHistory = (
  transaction: TransactionHistoryItem
): IDepositHistory => ({
  id: transaction.id,
  type: formatTransactionType(transaction.type),
  walletDetail: resolveWalletDetail(transaction),
  currency: formatCurrency(transaction.currency),
  transactionId: transaction.id,
  status: formatStatus(transaction.status),
  date: formatDateTime(transaction.createdAt),
  amount: transaction.amount?.toString() ?? "0",
});

const mapToWithdrawalHistory = (
  transaction: TransactionHistoryItem
): IWithdrawalHistory => ({
  id: transaction.id,
  type: formatTransactionType(transaction.type),
  walletDetail: resolveWalletDetail(transaction),
  currency: formatCurrency(transaction.currency),
  transactionId: transaction.id,
  status: formatStatus(transaction.status),
  fee: "—",
  date: formatDateTime(transaction.createdAt),
  transactionType: transaction.type,
  amount: transaction.amount?.toString() ?? "0",
});

export const buildDepositHistory = (
  transactions?: TransactionHistoryResponse,
  options?: TransactionHistoryFilterOptions
) => {
  if (!transactions || transactions.length === 0) {
    return [];
  }

  const predicate = createTransactionPredicate(options);

  return transactions
    .filter((transaction) => DEPOSIT_TRANSACTION_TYPE_SET.has(transaction.type))
    .filter(predicate)
    .map(mapToDepositHistory);
};

export const buildWithdrawalHistory = (
  transactions?: TransactionHistoryResponse,
  options?: TransactionHistoryFilterOptions
) => {
  if (!transactions || transactions.length === 0) {
    return [];
  }

  const predicate = createTransactionPredicate(options);

  return transactions
    .filter((transaction) =>
      WITHDRAWAL_TRANSACTION_TYPE_SET.has(transaction.type)
    )
    .filter(predicate)
    .map(mapToWithdrawalHistory);
};

export const getTransactionTypeLabel = (type: string) =>
  formatTransactionType(type);

export const getTransactionStatusLabel = (status: string) =>
  formatStatus(status);

export type { TransactionHistoryFiltersType as TransactionHistoryFilters };
